import { useCallback, useEffect } from "react";
import {
  Container,
  Row,
  Form,
  FloatingLabel,
  Button,
  Spinner,
  Image,
} from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import styles from "./registerPage.module.scss";
import registerPageSlice from "./store/slice";
import Logo from "../../images/logo-colour.svg";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment";
import { useRecaptcha } from "../../hooks/recaptcha";
import log from "../../utils/logger";

export type RegisterFormValues = {
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  companyName: string;
  companyRegistrationCode: string;
  companyVatCode: string;
  companyAddress: string;
  preferedDirections: string;
  recaptchaToken: string;
};

const RegisterPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isLoading = useSelector((state) => state.registerPage.isLoading);
  const requestSent = useSelector((state) => state.registerPage.requestSent);

  const recaptcha = useRecaptcha();

  const sendRequest = useCallback(
    (data: RegisterFormValues) =>
      dispatch(registerPageSlice.actions.sendRequest(data)),
    [dispatch]
  );

  const reset = useCallback(
    () => dispatch(registerPageSlice.actions.reset()),
    [dispatch]
  );

  const {
    formState: { errors },
    register,
    handleSubmit,
  } = useForm<RegisterFormValues>();

  const onSubmit: SubmitHandler<RegisterFormValues> = async (data) => {
    let recaptchaToken = "";

    try {
      recaptchaToken = await recaptcha.validate();
    } catch (error) {
      log.error(error as string);
    }

    if (recaptchaToken) {
      sendRequest({
        ...data,
        recaptchaToken,
      });
    }
  };

  useEffect(() => {
    if (requestSent) {
      navigate("/");
    }

    return () => {
      reset();
    };
  }, [navigate, requestSent, reset]);

  return (
    <Container fluid className={styles.container}>
      <Row className={styles.row}>
        <Form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
          <Image alt="logo" src={Logo} className={styles.logo} />

          <div>
            <h5 style={{ textAlign: "left" }}>Contact person</h5>

            <FloatingLabel
              className={styles.input}
              controlId="contactName"
              label="Full name*"
            >
              <Form.Control
                type="text"
                placeholder=" "
                isInvalid={!!errors.contactName}
                {...register("contactName", {
                  required: true,
                })}
                maxLength={100}
                disabled={isLoading}
              />
            </FloatingLabel>

            <FloatingLabel
              className={styles.input}
              controlId="contactEmail"
              label="Email*"
            >
              <Form.Control
                type="email"
                placeholder=" "
                isInvalid={!!errors.contactEmail}
                {...register("contactEmail", {
                  required: true,
                })}
                maxLength={100}
                disabled={isLoading}
              />
            </FloatingLabel>

            <FloatingLabel
              className={styles.input}
              controlId="contactPhone"
              label="Phone*"
            >
              <Form.Control
                type="text"
                placeholder=" "
                isInvalid={!!errors.contactPhone}
                {...register("contactPhone", {
                  required: true,
                })}
                maxLength={100}
                disabled={isLoading}
              />
            </FloatingLabel>
          </div>

          <div>
            <h5 style={{ textAlign: "left" }}>Company data</h5>

            <FloatingLabel
              className={styles.input}
              controlId="companyName"
              label="Name*"
            >
              <Form.Control
                type="text"
                placeholder=" "
                isInvalid={!!errors.companyName}
                {...register("companyName", {
                  required: true,
                })}
                maxLength={100}
                disabled={isLoading}
              />
            </FloatingLabel>

            <FloatingLabel
              className={styles.input}
              controlId="companyRegistrationCode"
              label="Registration code*"
            >
              <Form.Control
                type="text"
                placeholder=" "
                isInvalid={!!errors.companyRegistrationCode}
                {...register("companyRegistrationCode", {
                  required: true,
                })}
                maxLength={100}
                disabled={isLoading}
              />
            </FloatingLabel>

            <FloatingLabel
              className={styles.input}
              controlId="companyVatCode"
              label="VAT code*"
            >
              <Form.Control
                type="text"
                placeholder=" "
                isInvalid={!!errors.companyVatCode}
                {...register("companyVatCode", {
                  required: true,
                })}
                maxLength={100}
                disabled={isLoading}
              />
            </FloatingLabel>

            <FloatingLabel
              className={styles.input}
              controlId="companyAddress"
              label="Address*"
            >
              <Form.Control
                type="text"
                placeholder=" "
                isInvalid={!!errors.companyAddress}
                {...register("companyAddress", {
                  required: true,
                })}
                maxLength={100}
                disabled={isLoading}
              />
              <Form.Text style={{ display: "inherit", textAlign: "left" }}>
                Street, City, Country, ZIP code
              </Form.Text>
            </FloatingLabel>

            <FloatingLabel
              className={styles.input}
              controlId="preferedDirections"
              label="Prefered directions"
            >
              <Form.Control
                as="textarea"
                placeholder=" "
                isInvalid={!!errors.preferedDirections}
                {...register("preferedDirections", {
                  required: false,
                })}
                style={{ height: "100px" }}
                disabled={isLoading}
              />
              <Form.Text style={{ display: "inherit", textAlign: "left" }}>
                Example: Germany to Sweden, France to United Kingdom, ...
              </Form.Text>
            </FloatingLabel>
          </div>

          <Button
            type="submit"
            size="lg"
            className={styles.submit}
            disabled={isLoading}
          >
            Register
            {isLoading && (
              <Spinner
                size="sm"
                animation="border"
                className={styles.spinner}
              />
            )}
          </Button>

          <div className={styles.goBack}>
            <Link to="/">Go back</Link>
          </div>

          <p className={styles.copyright}>
            © {moment().format("YYYY")} Girteka Competence Center
          </p>
        </Form>
      </Row>
    </Container>
  );
};

export default RegisterPage;
